import React, { forwardRef, useEffect } from "react";
import { useLocalStore, Observer } from "mobx-react";
import { FormControlLabel, Grid } from "@material-ui/core";
import locale from "@bonliva-crm/locale";
import { useNavigation } from "@navigation";
import {
  BookingFinanceEditor_Fetch,
  BookingFinanceSearch_Get,
  useBookingFinanceEditor_FetchQuery,
  BookingFinanceEditor_ItemFragment
} from "@graphql";
import { getObservableQuery } from "@graphql/getObservableQuery";
import { Spinner, ErrorCard } from "@components";
import { BookingFinanceForm } from "./BookingFinanceForm";
import { useBookingInvoiceDialog } from "../BookingInvoiceDialog/useBookingInvoiceDialog";
import { BookingFinanceFormStore } from "./BookingFinanceEditorStore";
import { BookingInvoicing } from "../BookingInvoicing/BookingInvoicing";
import MuiSwitch from "@material-ui/core/Switch";
import { store } from "@store";

type Props = {
  bookingId: string;
  bookingData: BookingFinanceEditor_ItemFragment;
  forceLoad: boolean;
  invoiceFormRef: any;
};

export const BookingFinanceEditor = forwardRef<BookingFinanceFormStore, Props>(
  (props, ref) => {
    const navigation = useNavigation();

    const form = useLocalStore(() => new BookingFinanceFormStore());

    const booking = useBookingFinanceEditor_FetchQuery({
      variables: { id: props.bookingId },
      fetchPolicy: "network-only",
      skip: !props.forceLoad,
      onCompleted: (data) => form.init(data.bookingById)
    });

    useEffect(() => {
      props.bookingData && !props.forceLoad && form.init(props.bookingData);
    }, [props.bookingId]);

    const invoiceDialog = useBookingInvoiceDialog({
      withCustomNumber: true,
      costCenterId: props.bookingData.CostCenter?.Id ?? "",
      onSubmit: ({ invoiceNumberShort }) => {
        form.values.Invoices = [...form.values.Invoices, invoiceNumberShort].unique();
        form.validate();
      }
    });

    (ref as any).current = form;

    if (booking.loading) {
      return <Spinner />;
    }

    if (booking.error) {
      return (
        <ErrorCard
          title={locale.ErrorOccurred}
          message={booking.error.toString()}
          centered
        />
      );
    }

    return (
      <Grid container spacing={0}>
        <Grid item xs={12} lg={6}>
          <BookingFinanceForm
            bookingData={props.bookingData}
            form={form}
            onClose={() => navigation.close()}
            invoiceDialog={invoiceDialog.dialog}
            openInvoiceDialog={() => invoiceDialog.open()}
            fetchBookingData={() =>
              booking.refetch().then((res) => {
                return form.init(res.data.bookingById);
              })
            }
          />
        </Grid>

        <Grid item xs={12} lg={6} style={{ paddingTop: 16, paddingRight: 16 }}>
          <Observer>
            {() => (
              <>
                <BookingInvoicing
                  ref={props.invoiceFormRef}
                  billingInstructions={
                    props.bookingData.BookingGroup.hospital.billingInstructions
                  }
                  bookingDate={props.bookingData.Week}
                  costCenterId={props.bookingData.CostCenter?.Id ?? ""}
                  bookingId={props.bookingId}
                  consultantFirstName={props.bookingData.Consultant.FirstName}
                  consultantLastName={props.bookingData.Consultant.LastName}
                  missedFields={form.missedFields}
                  canAddPayForm={form.canAddPayForm}
                  consultantType={
                    form.values.LegalPersonType === "Employee"
                      ? "employee"
                      : form.values.LegalPersonType === "Subcontractor"
                      ? "subcontractor"
                      : ""
                  }
                  isOwnProject={form.isOwnProject}
                  isProjectManager={form.isProjectManager}
                  isGroupInvoicing={props.bookingData.GroupInvoicing}
                  isDirtyBookingFinanceForm={form.customDirty}
                  onFormClosed={() => {
                    setTimeout(() => {
                      booking.refetch().then((res) => {
                        return form.init(res.data.bookingById);
                      });

                      getObservableQuery(
                        BookingFinanceEditor_Fetch.OperationName
                      )?.refetch();

                      getObservableQuery(
                        BookingFinanceSearch_Get.OperationName
                      )?.refetch();
                    }, 2000);
                  }}
                />
                {store().user.can("booking-heroma:update:all") && (
                  <div>
                    <FormControlLabel
                      control={
                        <MuiSwitch
                          checked={form.unlockForms}
                          onChange={(_, checked) => form.setUnlockForms(checked)}
                        />
                      }
                      label={locale.UnlockForms}
                      labelPlacement={"start"}
                    />
                  </div>
                )}
              </>
            )}
          </Observer>
        </Grid>
      </Grid>
    );
  }
);
